import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  UserCircle, 
  VideoCamera, 
  Handshake,
  ArrowRight,
  CheckCircle,
  Sparkle,
  ChatCircle,
  ShieldCheck,
  Star
} from '@phosphor-icons/react';

const HomePage = () => {
  const steps = [
    {
      number: "01",
      icon: UserCircle,
      title: "Complete Your Application",
      description: "Fill out our confidential application. This helps us understand who you are, what you desire and what you're looking for in a relationship."
    },
    {
      number: "02",
      icon: ChatCircle,
      title: "Virtual Consultation",
      description: "Book a one-on-one virtual consultation where we get to know you personally and discuss your relationship goals."
    },
    {
      number: "03",
      icon: Heart,
      title: "We Find Your Match",
      description: "Your matchmaker begins an intentional search. Matches are carefully curated based on mind, heart, faith and lifestyle alignment."
    },
    {
      number: "04",
      icon: VideoCamera,
      title: "Connect & Flourish",
      description: "Meet your match through guided virtual introductions with ongoing support and encouragement."
    }
  ];

  const features = [
    "Personalized matchmaking",
    "One-on-one virtual introductions",
    "Guided conversations & date support",
    "Respectful, mature, no-pressure approach",
    "Quality over quantity",
    "Faith-centered connections"
  ];

  const values = [
    { icon: Heart, title: "Laughter & Good Vibes", description: "A social dating experience designed to bring singles together through laughter and real conversation" },
    { icon: ShieldCheck, title: "Safe & Respectful", description: "Relaxed, no-pressure events where connections happen naturally in a respectful environment" },
    { icon: Sparkle, title: "Intentional Matching", description: "We connect based on personality, values and real-life compatibility. Not endless swiping" }
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden rose-gradient">
        {/* Decorative Hearts */}
        <div className="absolute top-20 right-20 opacity-10">
          <Heart weight="fill" className="w-64 h-64 text-primary animate-float" />
        </div>
        <div className="absolute bottom-20 left-10 opacity-10">
          <Heart weight="fill" className="w-40 h-40 text-amber-400 animate-float" style={{animationDelay: '1s'}} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full text-primary text-sm font-medium shadow-sm">
                <Heart weight="fill" className="w-4 h-4" />
                Online Matchmaking Service
              </div>
              
              <div>
                <h1 className="script-font text-5xl md:text-6xl lg:text-7xl text-primary mb-4">
                  Link, Laugh & Love
                </h1>
                <p className="font-serif text-3xl md:text-4xl text-gray-800 leading-tight">
                  Real Love Starts with a<br />
                  <span className="text-primary">Real Connection</span>
                </p>
                <p className="text-lg text-primary/80 mt-4 italic font-medium">
                  Connect with a purpose. Love blooms when the heart is ready.
                </p>
              </div>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                We connect intentional singles through personality, values and real-life 
                compatibility. Not endless swiping. We take the time to get to know you, 
                so your connections feel natural, comfortable and genuine.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button 
                    className="btn-primary text-lg px-10 py-6 w-full sm:w-auto"
                    data-testid="hero-apply-btn"
                  >
                    <Heart weight="fill" className="w-5 h-5 mr-2" />
                    Start Your Journey
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button 
                    variant="outline" 
                    className="btn-secondary text-lg px-10 py-6 w-full sm:w-auto"
                    data-testid="hero-how-it-works-btn"
                  >
                    How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Feature Card - Resume Style */}
            <div className="hidden md:block animate-fade-in-up stagger-2">
              <div className="resume-card rounded-lg p-8 space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="gold-accent"></div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Why Choose Us</span>
                </div>
                <h3 className="font-serif text-2xl text-gray-800">What Makes Us Different</h3>
                <ul className="space-y-4">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-muted-foreground">
                      <Heart weight="fill" className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Imagery Section (replaces the former Mission section) */}
      <section className="py-24 md:py-32 bg-white" data-testid="brand-imagery-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-stretch">
            <div
              className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square bg-stone-100"
              data-testid="brand-image-1"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_633bc2f0-2305-4f60-ba94-52fa72b4a97a/artifacts/z9gpxoab_b_i_need_a_cool_style_.png"
                alt="Link hearts, Laugh together, Love deeply — Link, Laugh & Love brand artwork"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>

            <div
              className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square bg-stone-100"
              data-testid="brand-image-2"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_633bc2f0-2305-4f60-ba94-52fa72b4a97a/artifacts/40qbezps_qwen-image-edit_a_i_need_a_cool_style_.png"
                alt="Everyone deserves a chance at love — Link, Laugh & Love brand artwork"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 rose-gradient">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-primary uppercase tracking-widest text-xs font-semibold">
              Our Values
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 mb-6 text-gray-800">
              Built on Trust & Authenticity
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index} 
                className={`paper-card rounded-lg p-8 text-center animate-fade-in-up stagger-${index + 1}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                  <value.icon weight="light" className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-serif text-xl mb-3 text-gray-800">{value.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-primary uppercase tracking-widest text-xs font-semibold">
              The Process
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 mb-6 text-gray-800">
              Steps to Get Started
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Your journey to finding meaningful connection begins with four simple steps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className={`resume-card rounded-lg p-8 animate-fade-in-up stagger-${index + 1}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="script-font text-4xl text-primary/30">{step.number}</span>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon weight="light" className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <h3 className="font-serif text-lg mb-3 text-gray-800">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial/Quote Section */}
      <section className="py-24 md:py-32 navy-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="flex justify-center gap-1 mb-8">
            {[1,2,3,4,5].map((_, i) => (
              <Star key={i} weight="fill" className="w-6 h-6 text-amber-400" />
            ))}
          </div>
          <p className="script-font text-3xl md:text-4xl text-white mb-8 leading-relaxed">
            "You are not navigating love alone. We provide continued communication, 
            encouragement and feedback throughout your matchmaking experience."
          </p>
          <div className="gold-accent mx-auto mb-4"></div>
          <p className="text-pink-200 font-medium">Link, Laugh & Love Team</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 rose-gradient relative overflow-hidden">
        <div className="absolute top-10 right-10 opacity-5">
          <Heart weight="fill" className="w-96 h-96 text-primary" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6 text-gray-800 animate-fade-in-up">
            Ready to Find Your Match?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto animate-fade-in-up stagger-1">
            Take the first step toward a meaningful connection. Complete your application 
            today and let us help you find your perfect match.
          </p>
          <Link to="/signup" className="animate-fade-in-up stagger-2 inline-block">
            <Button 
              className="btn-primary text-lg px-12 py-6"
              data-testid="cta-apply-btn"
            >
              <Heart weight="fill" className="w-5 h-5 mr-2" />
              Start Your Journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
};

export default HomePage;
